import React, { useEffect, createContext, useContext, useState, Dispatch, SetStateAction } from 'react';
import useForm from './components/FormHook';
import { FormFields } from './components/FormTypes';
import { ProviderSelection, JobFormFields, IPayScale } from './JobRequestTypes';
import { initialForm } from './JobForm';
import submitJobRequest, { JobType } from './JobRequestSubmission';
import client from 'shared/ApiClient';
import { Job, Provider } from 'models';
import { Kid } from 'models/Kid';
import { JobType as JobTypeModel } from '../../models/JobType';
import { track } from 'shared/Tracking';
import { timeWithDate } from './time-operations';
import { useUserContext } from 'UserContext';
import UserAttributesApi from 'shared/UserAttributesApi';
import { JobFormPresets, useModalsContext } from 'ModalsContext';
import { isLoggedIn } from 'api/UserApi';
import moment from 'moment';
import { submitRequest, createOngoingRequest as oneTimeCreateOngoingRequest } from './OnetimeJobSubmission';
import { getBusinessPreferences } from 'api/BusinessPreferenceApi';
import { BusinessPreference } from 'parent-portal/profile/BusinessProfile/BusinessPreferences/models';
import { setBreakMinutes } from './businessPreferenceMethods';
import { consoleLogInDev } from 'shared';
interface JobFormContextType {
    form: FormFields<JobFormFields>;
    currentPage: number;
    pageCount: number;
    goToNext: () => void;
    goToPrevious: () => void;
    kidChoices: Kid[];
    familyHasKids: boolean;
    familyHasPets: boolean;
    pastProviders: ProviderSelection[];
    preferredProviders: ProviderSelection[];
    jobTypes: JobTypeModel[];
    previouslyAgreedToTandemPay: boolean;
    loading: boolean;
    invitedProvider?: Provider;
    jobSubmissionCompleted: boolean;
    submit: () => Promise<Job | undefined>;
    createdJob: Job | undefined;
    setPageCount: (n: number) => void;
    setFamilyId: (id: number) => void;
    reset: () => void;
    simple: boolean;
    toggleSimpleForm: () => void;
    defaultPayScales: IPayScale[];
    updateDefaultPayScales: (payScales: IPayScale[]) => void;
    businessPreferences: BusinessPreference[];
    formErrors: Array<string>;
    setFormErrors: Dispatch<SetStateAction<never[]>>;
}
interface JobFormContextProps {
    children?: React.ReactNode;
}

interface PastProvider {
    id: number;
    first_name: string;
    last_name: string;
    babysitter_id: number;
}

const defaultPageCount = 8;

export const JobFormContext = createContext<JobFormContextType>(undefined as any);

export function JobFormContextProvider({ children }: JobFormContextProps) {
    const [formErrors, setFormErrors] = useState([]);

    const { user, updateHasPostedJob, role } = useUserContext();
    const [invitedProvider, setInvitedProvider] = useState<Provider>();

    const [currentPage, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(5);
    const [lastPage, setLastPage] = useState(defaultPageCount - 1);
    const [createdJob, setCreatedJob] = useState<Job>();
    const [jobSubmissionCompleted, setJobSubmissionCompleted] = useState(false);

    const [pastProviders, setPastProviders] = useState<ProviderSelection[]>([]);
    const [preferredProviders, setPreferredProviders] = useState<ProviderSelection[]>([]);

    const [kidChoices, setKidChoices] = useState<Kid[]>([]);
    const [familyHasKids, setFamilyHasKids] = useState(false);
    const [familyHasPets, setFamilyHasPets] = useState(false);

    const [jobTypes, setJobTypes] = useState<JobTypeModel[]>([]);
    const [previouslyAgreedToTandemPay, setPreviouslyAgreedToTandemPay] = useState(false);
    const [businessPreferences, setBusinessPreferences] = useState<BusinessPreference[]>([]);

    const [familyId, setFamilyId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [simple, setSimple] = useState(true);
    const [jobId, setJobId] = useState(0);
    const [defaultPayScales, setDefaultPayScales] = useState<IPayScale[]>([]);
    const form = useForm<JobFormFields>(initialForm(role === 'business_active'));
    const { closeJobForm, jobFormPresets } = useModalsContext();

    useEffect(() => {
        setLastPage(pageCount - 1);
    }, [pageCount]);

    useEffect(() => {
        getValuesForJobForm().then((info) => {
            form.fieldStates.kids.setValue(info.kids);
            handleKidsPets(info.kids);
            setPastProviders(info.pastProviders);
            setPreferredProviders(info.preferredProviders);
            setJobTypes(info.jobTypes);
            setPreviouslyAgreedToTandemPay(info.acceptedTandemPayAgreement);
            setBusinessPreferences(info.businessPreferences);
            setLoading(false);

            if (info?.businessPreferences?.length) {
                setBreakMinutes(info.businessPreferences, form.fieldStates.breakRequired, form.fieldStates.breakLength);
            }
        });
        getPayScales();
    }, []);

    useEffect(() => {
        jobFormPresets && setPresets(jobFormPresets);
    }, [jobFormPresets]);

    function handleKidsPets(kids: any) {
        setKidChoices(kids);
        setFamilyHasKids(kids.filter((kid: Kid) => kid.type === 'CHILD').length > 0);
        setFamilyHasPets(kids.filter((kid: Kid) => kid.type === 'PET').length > 0);
    }

    async function reloadKidsWhenEmtpy(jobTypeId: number) {
        if ((jobTypeId === 1 && !familyHasKids) || (jobTypeId === 2 && !familyHasPets)) {
            const { kids } = await getFamilyKids();
            handleKidsPets(kids);
        }
    }

    function getPayScales() {
        client('api/pay-scale/').then(setDefaultPayScales);
    }

    function updateDefaultPayScales(payScales: IPayScale[]) {
        const newDefaults = [...defaultPayScales];
        payScales.forEach((scale) => {
            let oldDefaultIndex = defaultPayScales.findIndex((oldScale) => oldScale.rate === scale.rate);
            if (oldDefaultIndex >= 0) {
                newDefaults[oldDefaultIndex].description = scale.description;
            } else {
                newDefaults.push({ ...scale });
            }
        });
        setDefaultPayScales(newDefaults);
    }

    useEffect(() => {
        if (form.fieldStates.requestType.value === 'recurring') setSimple(false);
    }, [form.fieldStates.requestType.value]);

    function setPresets(presets: JobFormPresets) {
        const {
            providerToBook,
            providerToBookDirect,
            frequency,
            jobType,
            headline,
            description,
            simple,
            pageNumber,
            pay,
            pastProviders,
            jobTimes,
            startMonth,
            startDate,
            endDate,
            daysOfTheWeek,
            draftJobId,
            sameTimes,
            jobDate,
            invitedProvider,
            isQuickFill,
            jobQualifications,
            payType,
            businessJobType,
            multipleProviders,
            jobDetails,
            breakRequired,
            breakLength,
            slotsAvailable,
            address,
        } = presets;
        if (simple !== undefined) setSimple(simple);
        if (pageNumber) setPage(pageNumber);
        if (frequency) form.fieldStates.requestType.setValue(frequency);
        if (headline) form.fieldStates.headline.setValue(headline);
        if (description) form.fieldStates.comments.setValue(description);
        if (providerToBookDirect) {
            form.fieldStates.pastProviders.setValue([providerToBookDirect]);
        } else if (providerToBook) {
            const preferredProvider = preferredProviders.find((x: any) => x.providerId === providerToBook);
            if (preferredProvider) {
                form.fieldStates.preferredProviders.setValue([preferredProvider]);
            } else {
                const pastProvider = pastProviders?.find((x: any) => x.providerId === providerToBook);
                if (pastProvider) {
                    form.fieldStates.pastProviders.setValue([pastProvider]);
                }
            }
        }
        if (jobType) {
            form.fieldStates.jobTypeId.setValue(jobType);
        }
        if (pay) form.fieldStates.pay.setValue(pay);
        if (pastProviders) form.fieldStates.pastProviders.setValue(pastProviders);
        if (jobTimes?.start && jobTimes?.end) form.fieldStates.jobTimes.setValue(jobTimes);
        if (startMonth) form.fieldStates.startEstimateMonth.setValue(startMonth);
        if (startDate) form.fieldStates.startDate.setValue(startDate);
        if (endDate) form.fieldStates.endDate.setValue(endDate);
        if (daysOfTheWeek) form.fieldStates.daysOfTheWeek.setValue(daysOfTheWeek);
        setInvitedProvider(invitedProvider);
        if (draftJobId) setJobId(draftJobId);
        if (sameTimes !== undefined) form.fieldStates.sameTimes.setValue(sameTimes);
        if (jobDate) form.fieldStates.jobDate.setValue(jobDate);
        if (isQuickFill !== undefined) form.fieldStates.isQuickFill.setValue(isQuickFill);
        if (jobQualifications) form.fieldStates.jobQualifications.setValue(jobQualifications);
        if (payType) form.fieldStates.payType.setValue(payType);
        if (businessJobType) form.fieldStates.businessJobType.setValue(businessJobType);
        if (multipleProviders) form.fieldStates.multipleProviders.setValue(multipleProviders);
        if (jobDetails) form.fieldStates.jobDetails.setValue(jobDetails);
        if (breakRequired) form.fieldStates.breakRequired.setValue(breakRequired);
        if (breakLength) form.fieldStates.breakLength.setValue(breakLength);
        if (slotsAvailable) form.fieldStates.slotsAvailable.setValue(slotsAvailable);
        if (address) form.fieldStates.address.setValue(address);
    }

    async function goToNext() {
        try {
            if (currentPage > 0) {
                const res = await updateDraft();
                setJobId(res.id);
            }

            goToNextPage();
        } catch (e: any) {
            consoleLogInDev(e);
            setFormErrors(e.errors);
        }
    }

    function goToNextPage() {
        track('Job Form next button clicked on page', { currentPage });

        if (currentPage === 0) {
            track('Job Form Job Type on Next', { jobTypeId: form.fieldStates.jobTypeId.value });
        }

        if (currentPage < lastPage) {
            setPage(currentPage + 1);
        }
    }

    function reset() {
        form.reset(initialForm(role === 'business_active'));
        setPage(0);
        setInvitedProvider(undefined);
        setCreatedJob(undefined);
        setJobSubmissionCompleted(false);
        setSimple(true);
        setJobId(0);
        const addresses = user?.address;
        if (addresses) {
            const mailingAddress = addresses.find((x) => x.address_type === 'PRIMARY');
            const addressId = mailingAddress ? mailingAddress.id : addresses[0].id;

            form.fieldStates.address.setValue([addressId]);
        }
    }

    async function updateDraft() {
        const now = moment().toISOString();
        let family_id = familyId ?? user?.family[0].id ?? 0;
        let formValues = form.toValues();
        let request = null;
        request =
            formValues.requestType === 'onetime'
                ? await oneTimeCreateOngoingRequest(now, formValues, family_id, 'DRAFT')
                : await submitJobRequest({
                      familyId: family_id,
                      payMin: formValues.pay.payMin,
                      payMax: formValues.pay.payMax,
                      jobOptions: {
                          daysOfTheWeek: formValues.daysOfTheWeek,
                          pastSitters: formValues.pastProviders.map(
                              (provider: { name: string; firstName: string; id: number; providerId: number }) =>
                                  provider.id,
                          ),
                          kids: [1, 2].includes(formValues.jobTypeId)
                              ? formValues.kids.map((kid: { id: number; name: string }) => kid.id)
                              : [],
                          sameTimes: formValues.sameTimes,
                          start: formValues.sameTimes
                              ? formValues.jobTimes.start &&
                                timeWithDate(formValues.jobTimes.start!, formValues.startDate!)
                              : formValues.startDate,
                          end: formValues.endDate,
                          end_time: formValues.jobTimes.end,
                          selectedPreferredProviders: formValues.preferredProviders.map(
                              (provider: { name: string; firstName: string; id: number; providerId: number }) =>
                                  provider.id,
                          ),
                          transportation: formValues.transportation ?? false,
                          multipleProviders: formValues.multipleProviders,
                          comments: formValues.comments,
                          startEstimateMonth: formValues.startEstimateMonth,
                          headline: formValues.headline,
                          paymentSchedule: formValues.paymentSchedule,
                          jobQualifications: formValues.jobQualifications,
                          address: formValues.address?.length ? formValues.address[0] : undefined,
                          pay_type: formValues.payType,
                          business_job_type: formValues.businessJobType,
                          jobDetails: formValues.jobDetails,
                          payScales: formValues.payScales,
                          breakRequired: formValues.breakRequired,
                          breakLength: formValues.breakLength,
                          slotsAvailable: formValues.slotsAvailable,
                      },
                      lastName: user?.last_name || formValues.userEmail,
                      jobType: JobType.ongoing,
                      preferredProviders: formValues.preferredProviders,
                      invitedProvider: invitedProvider ? { id: invitedProvider.id } : undefined,
                      jobTypeId: formValues.jobTypeId,
                      otherJobType: formValues.otherJobType,
                      acceptedTandemPayAgreement: formValues.acceptedTandemPayAgreement,
                      code: formValues.code,
                      status: 'DRAFT',
                      jobId: jobId,
                  });
        let res = await client(`api/ongoing/${jobId ? jobId : ''}`, {
            method: jobId ? 'PATCH' : 'POST',
            body: request,
        });
        return res;
    }

    async function submit() {
        track('Job Form submit button clicked on page');
        const formValues = form.toValues();
        if (loading) {
            return;
        }

        setLoading(true);
        let family_id = familyId ?? user?.family[0].id!;

        let newJob;
        try {
            if (formValues.requestType === 'onetime') {
                newJob = (await submitRequest(
                    form,
                    family_id,
                    user?.last_name || formValues.userEmail,
                    invitedProvider,
                    jobId,
                )) as any;
                setCreatedJob(newJob);
            } else {
                const jobPostDailyPay =
                    (formValues.payType && ['PAY_BY_DAY', 'MONTHLY_BUDGET'].includes(formValues.payType)) ||
                    formValues.businessJobType === 'TRIAL_RUN';
                let isOneTime = !jobPostDailyPay && (formValues.endDate?.diff(formValues.startDate, 'd') || 0) < 6;
                if (formValues.businessJobType) {
                    if ((formValues.endDate?.diff(formValues.startDate, 'd') || 0) > 1) {
                        isOneTime = false;
                    }
                }

                const submissionResult = (await submitJobRequest({
                    familyId: family_id,
                    payMin: formValues.pay.payMin,
                    payMax: formValues.pay.payMax,
                    jobOptions: {
                        daysOfTheWeek: formValues.daysOfTheWeek,
                        pastSitters: formValues.pastProviders.map(
                            (provider: { name: string; firstName: string; id: number; providerId: number }) =>
                                provider.id,
                        ),
                        kids: formValues.kids.map((kid: { id: number; name: string }) => kid.id),
                        sameTimes: formValues.sameTimes,
                        start: formValues.sameTimes
                            ? formValues.jobTimes.start &&
                              timeWithDate(formValues.jobTimes.start!, formValues.startDate!)
                            : formValues.startDate,
                        end: formValues.endDate,
                        end_time: formValues.jobTimes.end,
                        selectedPreferredProviders: formValues.preferredProviders.map(
                            (provider: { name: string; firstName: string; id: number; providerId: number }) =>
                                provider.id,
                        ),
                        transportation: formValues.transportation ?? false,
                        multipleProviders: formValues.multipleProviders,
                        comments: formValues.comments,
                        startEstimateMonth: formValues.startEstimateMonth,
                        headline: formValues.headline,
                        paymentSchedule: formValues.paymentSchedule,
                        jobQualifications: formValues.jobQualifications,
                        address: formValues.address,
                        pay_type: formValues.payType,
                        business_job_type: formValues.businessJobType,
                        jobDetails: formValues.jobDetails,
                        payScales: formValues.payScales,
                        breakRequired: formValues.breakRequired,
                        breakLength: formValues.breakLength,
                    },
                    lastName: user?.last_name || formValues.userEmail,
                    jobType: isOneTime ? JobType.onetime : JobType.ongoing,
                    preferredProviders: formValues.preferredProviders,
                    invitedProvider: invitedProvider ? { id: invitedProvider.id } : undefined,
                    jobTypeId: formValues.jobTypeId,
                    otherJobType: formValues.otherJobType,
                    acceptedTandemPayAgreement: formValues.acceptedTandemPayAgreement,
                    code: formValues.code,
                    status: 'ACTIVE',
                    jobId: jobId,
                })) as any;

                setCreatedJob(submissionResult.job);
                newJob = submissionResult.job;
            }
            reloadKidsWhenEmtpy(formValues.jobTypeId);
            updateHasPostedJob();
            setJobSubmissionCompleted(true);
        } finally {
            setLoading(false);
        }
        goToNextPage();
        return newJob;
    }

    function goToPrevious() {
        track('Job Form back button clicked on page', { currentPage });

        if (currentPage === 0) {
            reset();
            closeJobForm();
        } else {
            setPage(currentPage - 1);
        }
    }

    async function getValuesForJobForm() {
        let initialValue = { preferredProviders: [], pastProviders: [], kids: [], businessPrefences: [] };
        const values = await Promise.all([getJobTypes()]);
        const userValues = isLoggedIn()
            ? [
                  ...(await Promise.all([
                      getProviderChoices(),
                      getFamilyKids(),
                      getHasAgreedToTandemPay(),
                      getBusinessPreference(),
                  ])),
              ]
            : [{ transportation: false }];

        return Object.assign(initialValue, ...values, ...userValues);
    }

    function toggleSimpleForm() {
        setSimple(!simple);
    }

    const contextValue = {
        form,
        currentPage,
        setPageCount,
        setFamilyId,
        pageCount,
        goToNext,
        goToPrevious,
        createdJob,
        pastProviders,
        preferredProviders,
        jobTypes,
        kidChoices,
        familyHasKids,
        familyHasPets,
        previouslyAgreedToTandemPay,
        invitedProvider,
        loading,
        jobSubmissionCompleted,
        submit,
        reset,
        simple,
        toggleSimpleForm,
        defaultPayScales,
        updateDefaultPayScales,
        businessPreferences,
        formErrors,
        setFormErrors,
    };

    return <JobFormContext.Provider value={contextValue}>{children}</JobFormContext.Provider>;
}

export function useJobFormContext() {
    return useContext(JobFormContext);
}

function toProviderListItem(provider: PastProvider) {
    return {
        name: `${provider.first_name} ${provider.last_name}`,
        firstName: provider.first_name,
        id: provider.id,
        providerId: provider.babysitter_id,
    };
}

async function getProviderChoices() {
    const sitterChoices = await client('get-past-sitters/');
    const pastProviders = sitterChoices.past_sitters.map(toProviderListItem);
    const preferredProviders = sitterChoices.liked_providers.map(toProviderListItem);

    return { pastProviders, preferredProviders };
}

async function getFamilyKids() {
    const kidsResponse = await client('api/kids/');
    const kids = kidsResponse.map((kid: Kid) => ({ id: kid.id, name: kid.name, type: kid.type }));

    return { kids };
}

async function getJobTypes() {
    const jobTypes = (await client('api/job-types/')) as JobTypeModel[];
    jobTypes.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));

    return { jobTypes: [...jobTypes, { id: -1, name: 'Other' }] };
}

async function getHasAgreedToTandemPay() {
    const acceptedTandemPayAgreement = await UserAttributesApi.exists('Agreed to use TandemPay');
    return { acceptedTandemPayAgreement };
}

async function getBusinessPreference() {
    const businessPreferences = await getBusinessPreferences();
    return { businessPreferences };
}
